import { computed } from 'vue'
import type {
  JobFilterCustomDataProperty,
  JobFilterProperty,
  JobFilterType,
} from '@ironsight/types'
import { SyncTargetSchema, useAllSyncTargetSchemasQuery } from '@/api/schemas'
import { extractFilterableFields, FilterableField } from '@/utils/field-schema-helper'
import { upperFirst } from 'lodash'

const batchFilterableProperties: JobFilterProperty[] = [
  {
    name: 'BatchNumber',
    label: 'Batch Number',
    filterType: 'StringLookup',
    icon: 'request_quote',
    supportedComparisons: ['In'],
    visibilityType: 'Provider',
    canBeEmpty: true,
  },
  {
    name: 'BatchDate',
    label: 'Batch Date',
    icon: 'calendar_today',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
  },
  {
    name: 'DueDate',
    label: 'Due Date',
    icon: 'calendar_today',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
    canBeEmpty: true,
  },
  {
    name: 'CreatedDate',
    label: 'Date Created',
    icon: 'calendar_today',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
  },
  {
    name: 'Creator',
    label: 'Creator',
    icon: 'person',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'Customer',
    label: 'Customer',
    icon: 'business',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
  {
    name: 'QboSyncStatus',
    label: 'Qbo Sync Status',
    icon: 'sync',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
    canBeEmpty: true,
  },
  {
    name: 'QboSyncedAt',
    label: 'Qbo Synced At',
    icon: 'schedule',
    filterType: 'DateTime',
    supportedComparisons: ['Between'],
  },
  {
    name: 'QboSyncedBy',
    label: 'Qbo Synced By',
    icon: 'person_outline',
    filterType: 'StringLookup',
    supportedComparisons: ['In'],
  },
]

const CUSTOM_DATA_TYPE_TO_FILTER_TYPE: Record<string, JobFilterType> = {
  string: 'Text',
  boolean: 'Boolean',
  number: 'Double',
  integer: 'Integer',
}

const SCHEMA_LOOKUP_ENTITY_TO_FILTER_TYPE: Record<string, JobFilterType> = {
  user: 'StringLookup',
  location: 'IntLookup',
  business_unit: 'StringLookup',
  company: 'IntLookup',
}

const CUSTOM_DATA_TYPE_TO_ICON: Record<string, string> = {
  boolean: 'radio_button_checked',
  string: 'short_text',
  number: 'numbers',
  integer: 'numbers',
}

const CUSTOM_DATA_TYPE_TO_SUPPORTED_COMPARISONS: Record<string, string[]> = {
  string: ['Equals', 'Contains'],
  boolean: ['Equals'],
  number: ['Equals', 'GreaterThan', 'LessThan'],
  integer: ['Equals', 'GreaterThan', 'LessThan'],
}

const NESTED_SUPPORTED_COMPARISONS: Record<string, string[]> = {
  string: ['Equals', 'Contains'],
  boolean: ['Equals'],
  number: ['Equals'],
  integer: ['Equals'],
}

/**
 * Maps a field definition type to the backend FilterKind string used in the
 * composite key format: CustomData|{appType}|{FilterKind}|{pathKey}|{fieldTitle}
 */
function toFilterKind(field: FilterableField): string | null {
  const def = field.definition
  // Lookup fields use their entity-specific filter kind
  if (def.lookup?.entity) {
    const lookupFilterType = SCHEMA_LOOKUP_ENTITY_TO_FILTER_TYPE[def.lookup.entity]
    return lookupFilterType ?? null
  }
  if (def.type === 'boolean') return 'Boolean'
  if (def.type === 'integer') return 'Integer'
  if (def.type === 'number') return 'Decimal'
  if (def.type === 'string') {
    if (def.format === 'date' || def.format === 'date-time') return 'DateTime'
    if (def.enum) return 'StringLookup'
    return 'Text'
  }
  return null
}

/**
 * Builds custom data filter properties from sync target schemas using
 * extractFilterableFields to properly handle nested and array fields.
 * Key format: CustomData|{appType}|{FilterKind}|{pathKey}|{fieldTitle}
 */
function buildCustomDataFilterProperties(
  schemas: SyncTargetSchema[],
): JobFilterCustomDataProperty[] {
  const results: JobFilterCustomDataProperty[] = []

  // Detect duplicate labels across sync targets so we can prefix with the target name.
  const labelCounts = new Map<string, number>()
  for (const schema of schemas) {
    const schemaObj = schema.schema?.schema
    if (!schemaObj) continue
    const fields = extractFilterableFields(
      schemaObj,
      schema.schema.uiSchema ?? {},
      schemaObj.definitions ?? {},
      true,
    )
    for (const field of fields) {
      labelCounts.set(field.label, (labelCounts.get(field.label) ?? 0) + 1)
    }
  }

  for (const schema of schemas) {
    const schemaObj = schema.schema?.schema
    if (!schemaObj) continue

    const fields = extractFilterableFields(
      schemaObj,
      schema.schema.uiSchema ?? {},
      schemaObj.definitions ?? {},
      true,
    )

    for (const field of fields) {
      const filterKind = toFilterKind(field)
      if (!filterKind) continue

      const isLookupField = Boolean(
        field.definition.lookup?.entity && field.definition.lookup.entity.length > 1,
      )
      const isEnumField = Boolean(field.definition.enum)
      const isDefinedOptionField = isEnumField || isLookupField

      const filterType: JobFilterType = isLookupField
        ? (SCHEMA_LOOKUP_ENTITY_TO_FILTER_TYPE[field.definition.lookup!.entity!] ?? 'StringLookup')
        : isEnumField
          ? 'StringLookup'
          : (CUSTOM_DATA_TYPE_TO_FILTER_TYPE[field.definition.type] ?? 'Text')

      const supportedComparisons = isDefinedOptionField
        ? ['In']
        : field.isNested
          ? (NESTED_SUPPORTED_COMPARISONS[field.definition.type] ?? ['Equals'])
          : (CUSTOM_DATA_TYPE_TO_SUPPORTED_COMPARISONS[field.definition.type] ?? ['Equals'])

      const needsPrefix = (labelCounts.get(field.label) ?? 0) > 1
      const label = needsPrefix ? `${schema.name} ${field.label}` : field.label

      const key = `CustomData|${schema.type}|${filterKind}|${field.pathKey}|${field.definition.title}`

      results.push({
        name: 'CustomData',
        label,
        filterType,
        icon: isDefinedOptionField
          ? 'radio_button_checked'
          : (CUSTOM_DATA_TYPE_TO_ICON[field.definition.type] ?? 'short_text'),
        supportedComparisons,
        key,
        taskIds: [],
        lookupEntity: isLookupField ? upperFirst(field.definition.lookup!.entity!) : undefined,
        definedOptions: isEnumField
          ? field.definition.enum!.map((v) => ({ label: v, value: v }))
          : undefined,
        canBeEmpty: !field.required,
      })
    }
  }

  return results
}

export function useBatchFilterProperties() {
  const basicFilterableProperties = computed<JobFilterProperty[]>(() => batchFilterableProperties)

  const { data: syncTargetSchemas } = useAllSyncTargetSchemasQuery()

  const customFilterableProperties = computed<JobFilterCustomDataProperty[]>(() =>
    buildCustomDataFilterProperties(syncTargetSchemas.value ?? []),
  )

  return {
    basicFilterableProperties,
    customFilterableProperties,
  }
}
